import { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Sparkles, Loader2, User as UserIcon, Trash2, Share2 } from 'lucide-react';
import { aiHelperApi, type ChatTurn } from '../api/ai_helper';
import { aiSendApi } from '../api/v2';
import { orgsApi, type Organization } from '../api/organizations';
import { ticketsApi, type TicketBrief } from '../api/tickets';
import { Markdown } from '../lib/markdown';

interface Props {
  /** id организации — добавляет «контекст: org» в промпт. Если не задан и user=client, берётся из его user.organization_id. */
  organizationId?: string | null;
  /** localStorage-ключ для сохранения истории. Если null — история не сохраняется между сессиями. */
  storageKey?: string | null;
  /** Заголовок над чатом */
  title?: string;
  /** Поясняющий текст */
  subtitle?: string;
}

interface Turn extends ChatTurn {
  sources?: Array<{ filename: string; score: number }>;
}

export default function AIChat({ organizationId, storageKey, title = 'AI-ассистент VALO', subtitle }: Props) {
  const storage = storageKey || null;
  const [turns, setTurns] = useState<Turn[]>(() => {
    if (!storage) return [];
    try { return JSON.parse(localStorage.getItem(storage) || '[]'); }
    catch { return []; }
  });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (storage) localStorage.setItem(storage, JSON.stringify(turns));
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, storage]);

  const send = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || busy) return;
    setErr(null);
    const history: ChatTurn[] = turns.map(t => ({ role: t.role, content: t.content }));
    const optimistic: Turn = { role: 'user', content: q };
    setTurns(prev => [...prev, optimistic]);
    setInput('');
    setBusy(true);
    try {
      const r = await aiHelperApi.ask(q, history, organizationId || undefined);
      setTurns(prev => [...prev, {
        role: 'assistant', content: r.answer,
        sources: r.sources.filter(s => (s.score ?? 0) >= 0.3).map(s => ({ filename: s.filename, score: s.score })),
      }]);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  const clear = () => {
    if (!window.confirm('Очистить историю диалога?')) return;
    setTurns([]);
    if (storage) localStorage.removeItem(storage);
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl flex flex-col"
             style={{ minHeight: 500 }}>
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white flex items-center gap-2">
          <Sparkles size={16} className="text-purple-400" /> {title}
        </h2>
        {turns.length > 0 && (
          <button onClick={clear} title="Очистить диалог"
            className="text-slate-400 hover:text-red-400 p-1">
            <Trash2 size={13} />
          </button>
        )}
      </header>

      {subtitle && (
        <p className="px-5 pt-3 text-[12px] text-slate-500">{subtitle}</p>
      )}

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {turns.length === 0 ? (
          <div className="text-center text-sm text-slate-500 py-12">
            <Sparkles className="mx-auto mb-2 text-purple-400" />
            Задайте вопрос ассистенту — он ищет ответ в документации VALO.
            <br />
            <span className="text-[11px] text-slate-600">Например: «как настроить SMTP» или «куда смотреть логи»</span>
          </div>
        ) : turns.map((t, i) => (
          <div key={i} className={`flex gap-2 ${t.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
              t.role === 'user' ? 'bg-blue-600' : 'bg-purple-900/60'
            }`}>
              {t.role === 'user' ? <UserIcon size={14} className="text-white" />
                                  : <Sparkles size={14} className="text-purple-300" />}
            </div>
            <div className={`max-w-[80%] min-w-0 rounded-xl px-3 py-2 ${
              t.role === 'user'
                ? 'bg-blue-600/15 border border-blue-700/40'
                : 'bg-slate-950/60 border border-slate-800'
            }`}>
              <div className="text-sm text-slate-100 break-words">
                {t.role === 'assistant'
                  ? <Markdown source={t.content} />
                  : <p className="whitespace-pre-wrap leading-relaxed">{t.content}</p>}
              </div>
              {t.role === 'assistant' && (
                <SendToOrgButton text={t.content} fixedOrgId={organizationId || undefined} />
              )}
              {t.sources && t.sources.length > 0 && (
                <div className="mt-2 text-[10px] text-slate-500 border-t border-slate-800 pt-1.5">
                  <span className="mr-1">Источники:</span>
                  {t.sources.map((s, j) => (
                    <span key={j} className="inline-block bg-slate-800 rounded px-1.5 py-0.5 mr-1 mb-1">
                      [{(s.score*100|0)}%] {s.filename}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Loader2 size={14} className="animate-spin text-purple-400" />
            <span>AI думает (ищет в документации и формулирует ответ)…</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {err && (
        <div className="mx-5 mb-2 bg-red-900/30 border border-red-800 text-red-300 px-3 py-1.5 rounded text-xs">{err}</div>
      )}

      <form onSubmit={send} className="border-t border-slate-800 p-3 flex items-end gap-2">
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          rows={2}
          placeholder="Спросите ассистента… (Enter — отправить, Shift+Enter — перенос)"
          className="flex-1 bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none resize-none" />
        <button type="submit" disabled={busy || !input.trim()}
          className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-4 py-2 rounded inline-flex items-center gap-1.5 self-stretch">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Отправить
        </button>
      </form>
    </section>
  );
}


interface SendResult {
  orgId: string;
  orgName: string;
  ok: boolean;
  sentToTg: boolean;
  sentToMax: boolean;
  sentToTicket: boolean;
  ticketNumber: number | null;
  notes: string[];
}

function SendToOrgButton({ text, fixedOrgId }:
  { text: string; fixedOrgId?: string }) {
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);

  // Выбор тикета: для какой организации загружены тикеты + сам выбор
  const [tickets, setTickets] = useState<TicketBrief[]>([]);
  const [ticketsFor, setTicketsFor] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<string>('');  // '' = не добавлять
  const [alsoTicket, setAlsoTicket] = useState(true);

  const loadTickets = (orgId: string) => {
    if (ticketsFor === orgId) return;
    ticketsApi.list({ organization_id: orgId, limit: 50 })
      .then(r => {
        const open_ = r.items.filter(t => !['closed','resolved','cancelled'].includes(t.status));
        setTickets(open_);
        setTicketsFor(orgId);
        if (open_.length > 0) setSelectedTicket(open_[0].id);
        else setSelectedTicket('');
      })
      .catch(() => { setTickets([]); setSelectedTicket(''); setTicketsFor(orgId); });
  };

  useEffect(() => {
    if (!open) return;
    if (orgs.length === 0) orgsApi.list().then(setOrgs).catch(() => {});
  }, [open]);

  // Если fixedOrgId — подтянем имя единожды + список тикетов
  const [fixedName, setFixedName] = useState<string>('');
  useEffect(() => {
    if (!fixedOrgId) return;
    orgsApi.get(fixedOrgId).then(o => setFixedName(o.name)).catch(() => setFixedName(fixedOrgId));
    loadTickets(fixedOrgId);
  }, [fixedOrgId]);

  const filtered = useMemo(() => {
    if (!q.trim()) return orgs.slice(0, 30);
    const s = q.toLowerCase();
    return orgs.filter(o =>
      o.name.toLowerCase().includes(s) || o.inn.includes(s)
    ).slice(0, 30);
  }, [orgs, q]);

  const send = async (orgId: string, orgName: string) => {
    const wantTicket = alsoTicket && !!selectedTicket;
    const tNum = tickets.find(t => t.id === selectedTicket)?.number;
    const lines = [
      `Отправить ответ ассистента в чаты «${orgName}»?`,
      `• Telegram + MAX (если привязаны)`,
    ];
    if (wantTicket && tNum) lines.push(`• В веб-чат тикета SD-${String(tNum).padStart(4,'0')}`);
    if (!window.confirm(lines.join('\n'))) return;
    setBusy(true); setResult(null); setOpen(false);
    try {
      const r = await aiSendApi.send(orgId, text, {
        ticket_id: wantTicket ? selectedTicket : undefined,
      });
      const ok = r.sent_to_tg || r.sent_to_max || r.sent_to_ticket;
      setResult({
        orgId, orgName, ok,
        sentToTg: r.sent_to_tg, sentToMax: r.sent_to_max,
        sentToTicket: r.sent_to_ticket,
        ticketNumber: r.ticket_number ?? null,
        notes: r.notes || [],
      });
    } catch (e: any) {
      setResult({
        orgId, orgName, ok: false,
        sentToTg: false, sentToMax: false,
        sentToTicket: false, ticketNumber: null,
        notes: [e?.response?.data?.detail || e?.message || 'Ошибка'],
      });
    } finally { setBusy(false); }
  };

  // ── Visible feedback после отправки ─────────────────────────────────────
  if (result) {
    const chans: string[] = [];
    if (result.sentToTg) chans.push('Telegram');
    if (result.sentToMax) chans.push('MAX');
    if (result.sentToTicket && result.ticketNumber != null)
      chans.push(`веб-чат SD-${String(result.ticketNumber).padStart(4,'0')}`);
    return (
      <div className={`mt-2 px-3 py-2 rounded border text-xs ${
        result.ok
          ? 'bg-emerald-900/20 border-emerald-800 text-emerald-100'
          : 'bg-red-900/30 border-red-800 text-red-100'
      }`}>
        <div className="flex items-start gap-2">
          <span className="text-base">{result.ok ? '✓' : '✗'}</span>
          <div className="flex-1 min-w-0">
            {result.ok ? (
              <>
                <div><b>Отправлено клиенту:</b> {result.orgName}</div>
                <div className="text-[11px] mt-0.5">
                  Каналы: {chans.map(c => (
                    <span key={c} className="inline-block bg-emerald-700/40 px-1.5 py-0.5 rounded mr-1">{c}</span>
                  ))}
                </div>
                {result.notes.length > 0 && (
                  <div className="text-[10px] text-emerald-300/70 mt-1">
                    {result.notes.join(' · ')}
                  </div>
                )}
              </>
            ) : (
              <>
                <div><b>Не отправлено:</b> {result.orgName}</div>
                <div className="text-[11px] mt-1 space-y-0.5">
                  {result.notes.length > 0
                    ? result.notes.map((n, i) => <div key={i}>· {n}</div>)
                    : <div>Нет привязанных чатов TG/MAX.</div>}
                </div>
              </>
            )}
          </div>
          <button onClick={() => setResult(null)}
                  className="text-slate-400 hover:text-white text-xs">×</button>
        </div>
      </div>
    );
  }

  // Фиксированный org (таб «Помощник» в карточке организации)
  if (fixedOrgId) {
    return (
      <div className="mt-2 inline-flex flex-col gap-1.5 max-w-md bg-slate-950/40 border border-slate-800 rounded p-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          Отправить клиенту: <b className="text-slate-300">{fixedName || '…'}</b>
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-slate-300">
          <input type="checkbox" checked={alsoTicket}
                 onChange={e => setAlsoTicket(e.target.checked)} />
          Также добавить в веб-чат тикета:
          <select value={selectedTicket} onChange={e => setSelectedTicket(e.target.value)}
            disabled={!alsoTicket || tickets.length === 0}
            className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-[11px] text-white max-w-[200px] truncate disabled:opacity-50">
            {tickets.length === 0 && <option value="">нет открытых тикетов</option>}
            {tickets.map(t => (
              <option key={t.id} value={t.id}>
                SD-{String(t.number).padStart(4,'0')} · {t.subject.slice(0, 35)}
              </option>
            ))}
          </select>
        </label>
        <button onClick={() => send(fixedOrgId, fixedName || 'этому клиенту')} disabled={busy}
          className="text-[11px] inline-flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-2.5 py-1 rounded">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Share2 size={11} />}
          Отправить
        </button>
      </div>
    );
  }

  // Глобальный /ai — двухшаговый picker: 1) выбрать org, 2) выбрать тикет
  const [pickedOrg, setPickedOrg] = useState<{ id: string; name: string } | null>(null);
  return (
    <div className="mt-2">
      {!open ? (
        <button onClick={() => setOpen(true)}
          className="text-[11px] inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded">
          <Share2 size={11} /> Отправить клиенту…
        </button>
      ) : pickedOrg ? (
        <div className="mt-1 bg-slate-950/60 border border-slate-700 rounded p-2 max-w-md">
          <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">
            Отправка: <b className="text-slate-300">{pickedOrg.name}</b>
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-300 mt-1">
            <input type="checkbox" checked={alsoTicket}
              onChange={e => setAlsoTicket(e.target.checked)} />
            Также в веб-чат тикета:
            <select value={selectedTicket} onChange={e => setSelectedTicket(e.target.value)}
              disabled={!alsoTicket || tickets.length === 0}
              className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-[11px] text-white max-w-[200px] truncate disabled:opacity-50">
              {tickets.length === 0 && <option value="">нет открытых</option>}
              {tickets.map(t => (
                <option key={t.id} value={t.id}>
                  SD-{String(t.number).padStart(4,'0')} · {t.subject.slice(0, 30)}
                </option>
              ))}
            </select>
          </label>
          <div className="flex gap-2 mt-2">
            <button onClick={() => send(pickedOrg.id, pickedOrg.name)} disabled={busy}
              className="text-[11px] flex-1 inline-flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-2.5 py-1 rounded">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Share2 size={11} />}
              Отправить
            </button>
            <button onClick={() => { setPickedOrg(null); }} disabled={busy}
              className="text-[11px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-2.5 py-1 rounded">
              Назад
            </button>
            <button onClick={() => { setOpen(false); setPickedOrg(null); }} disabled={busy}
              className="text-[11px] text-slate-500 hover:text-white px-2 py-1">×</button>
          </div>
        </div>
      ) : (
        <div className="mt-1 bg-slate-950/60 border border-slate-700 rounded p-2 max-w-md">
          <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Шаг 1: выбор организации</div>
          <input value={q} onChange={e => setQ(e.target.value)} autoFocus
            placeholder="Поиск по имени или ИНН…"
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white" />
          <ul className="max-h-48 overflow-y-auto mt-1">
            {filtered.length === 0 ? (
              <li className="text-[11px] text-slate-500 px-2 py-2">Ничего не найдено</li>
            ) : filtered.map(o => (
              <li key={o.id}>
                <button onClick={() => {
                    setPickedOrg({ id: o.id, name: o.name });
                    loadTickets(o.id);
                  }}
                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-800 rounded flex items-center gap-2">
                  <Share2 size={10} className="text-slate-500" />
                  <span className="flex-1 truncate">{o.name}</span>
                  <span className="text-[10px] text-slate-500 font-mono">{o.inn}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between mt-1">
            <button onClick={() => setOpen(false)}
              className="text-[10px] text-slate-500 hover:text-white">Закрыть</button>
          </div>
        </div>
      )}
    </div>
  );
}
